import { Divider } from "components/design/Divider";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { FilterContext } from "components/DesignSystem/Filter/FilterContext";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { Search } from "components/DesignSystem/Search/Search";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { DropDownItem } from "components/GeneralLedger/GeneralLedgerFilters";
import { Funnel } from "components/icons/Funnel";
import { PriceInput } from "components/PriceInput/PriceInput";
import { CASH_FLOW, CASH_FLOW_LABEL } from "constants/transaction";
import { Form, Formik } from "formik";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useGetAllAccountQuery } from "store/apis/transactions";
import { useGetAllTransactionMerchantsQuery } from "store/apis/merchant";
import {
  OpenItemFilterState,
  setOpenItemFilter,
} from "store/slices/openItemFilter";
import { RootState } from "store/store";
import { SourceFilter, TxnCategories } from "types/Models/books";
import { debounce } from "utils/debouncing";
import { flattenTypes } from "utils/flattenCOA";
import { getDatePeriod } from "utils/getDatePeriod";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Capsule, isChecked } from "./Filter/Filter";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";

const OpenItemDateFilter = () => {
  const dispatch = useDispatch();
  const { startDate, endDate, selectPeriod } = useSelector(
    (state: RootState) => state.openItemFilter
  );

  const { values, updateFilter } = useFilters({
    initialValue: {
      START_DATE: startDate,
      END_DATE: endDate,
      SELECT_PERIOD: selectPeriod,
    },
  });

  useEffect(() => {
    dispatch(
      setOpenItemFilter({
        startDate: values.START_DATE,
        endDate: values.END_DATE,
        selectPeriod: values.SELECT_PERIOD,
      })
    );
  }, [values.START_DATE, values.END_DATE, values.SELECT_PERIOD]);

  return (
    <BooksDateFilter
      datePeriod={getDatePeriod}
      values={values}
      updateFilter={updateFilter}
    />
  );
};

type MerchantOption = { uuid: string; name: string; txn_count: number };

const OpenItemVendorsFilter = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [merchantsData, setMerchantsData] = useState<MerchantOption[]>([]);

  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { vendors } = useSelector((state: RootState) => state.openItemFilter);

  const { data, isLoading, isSuccess } = useGetAllTransactionMerchantsQuery(
    { groupId, entityId, page_num: pageNumber, search_term: searchTerm },
    { skip: !groupId || !entityId, refetchOnMountOrArgChange: true }
  );

  const {
    merchants: merchantsRes = [],
    current_page = 1,
    total_pages = 1,
  } = data || {};

  const onSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchTerm(value || "");
    setPageNumber(1);
  });

  const onHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    let newVendors: Set<string>;

    if (checked) {
      newVendors = new Set([...vendors, value]);
    } else {
      newVendors = new Set(vendors.filter((v) => v !== value));
    }

    dispatch(setOpenItemFilter({ vendors: [...newVendors] }));
  };

  useEffect(() => {
    setPageNumber(current_page);
    setTotalPages(total_pages);
    const newMerchants = merchantsRes.map((m) => ({
      uuid: m.uuid,
      name: m.name,
      txn_count: m.txn_count ?? 0,
    }));
    if (searchTerm || pageNumber === 1) {
      setMerchantsData(newMerchants);
    } else {
      setMerchantsData((prev) => {
        const existingUuids = new Set(prev.map((m) => m.uuid));
        const toAdd = newMerchants.filter((m) => !existingUuids.has(m.uuid));
        return [...prev, ...toAdd];
      });
    }
  }, [merchantsRes, searchTerm]);

  const setPagination = debounce(() => {
    if (pageNumber < totalPages) {
      setPageNumber((prev) => prev + 1);
    }
  });

  const noMerchantsFound = merchantsData.length === 0;

  return (
    <div className="t-w-full t-flex t-flex-col t-gap-5 t-h-full">
      <Search
        block
        placeholder="Search Vendors"
        onChange={onSearch}
        customSize="regular"
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      />
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={noMerchantsFound}
      >
        <Async.Empty>
          <div className="t-flex t-items-center t-h-full t-justify-center">
            No vendor found
          </div>
        </Async.Empty>
        <Async.Success>
          <div
            onChange={onHandleChange}
            id="openItemScrollableDiv"
            className="t-flex t-flex-col t-h-[320px] t-overflow-y-auto t-overflow-x-hidden"
          >
            <InfiniteScroll
              dataLength={merchantsData?.length}
              next={setPagination}
              hasMore={pageNumber < totalPages}
              className="t-flex t-flex-col t-gap-3 t-px-2 t-h-[330px] t-overflow-y-auto t-overflow-x-hidden"
              scrollableTarget="openItemScrollableDiv"
              loader={<div className="t-w-full t-text-center">Loading...</div>}
            >
              {merchantsData?.map(({ uuid, name }) => (
                <div
                  key={uuid}
                  className="t-flex t-items-start t-gap-2 t-w-full t-min-w-0"
                >
                  <div className="t-min-w-0 t-flex-1">
                    <Checkbox
                      name={name}
                      value={uuid}
                      checked={Boolean(isChecked(vendors, uuid))}
                      label={name}
                    />
                  </div>
                </div>
              ))}
            </InfiniteScroll>
          </div>
        </Async.Success>
      </Async.Root>
    </div>
  );
};

const OpenItemCategoryFilter = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const { categoryIds } = useSelector(
    (state: RootState) => state.openItemFilter
  );

  const { chartOfAccounts } = useChartOfAccounts({
    search,
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });

  const onHandleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    types?: TxnCategories[]
  ) => {
    const { value, checked } = e.target;
    const selectedChilds =
      flattenTypes({ accounts: types! })?.map(({ uuid }) => uuid) || [];

    let newCategories: Set<string>;
    if (checked) {
      newCategories = new Set([...categoryIds, ...selectedChilds, value]);
    } else {
      newCategories = new Set(
        categoryIds.filter((id) => !selectedChilds.includes(id) && id !== value)
      );
    }

    dispatch(setOpenItemFilter({ categoryIds: [...newCategories] }));
  };

  const handleSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value.toLowerCase() || "");
  });

  const noCOAFound = chartOfAccounts.length === 0;

  return (
    <Dropdown.Root onOpenChange={() => setSearch("")}>
      <Dropdown.Trigger asChild>
        <div className="all:unset form-input w-full t-box-border t-flex t-h-10 t-w-[100%] t-items-center t-rounded t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-px-4 t-pr-5 t-font-sans t-text-body-sm !t-font-medium t-text-text-100 t-transition-all">
          <div className="t-overflow-hidden t-text-ellipsis t-whitespace-nowrap t-text-text-30">
            {categoryIds.length > 0
              ? `${categoryIds.length} categories selected`
              : "Select an account"}
          </div>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          sideOffset={6}
          align="start"
          className="t-relative t-w-[430px]"
        >
          <Search
            placeholder="Search..."
            onChange={handleSearch}
            customSize="regular"
            block
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          />
          {noCOAFound ? (
            <div className="t-my-1.5 t-flex t-h-48 t-items-center t-justify-center">
              No categories found
            </div>
          ) : (
            <div className="t-max-h-72 t-overflow-auto">
              <DropDownItem
                types={chartOfAccounts}
                level={0}
                onHandleChange={onHandleChange}
                categoryIds={categoryIds}
              />
            </div>
          )}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

const OpenItemFromFilter = () => {
  const dispatch = useDispatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const {
    data: froms = [],
    isLoading,
    isSuccess,
  } = useGetAllAccountQuery(
    { groupId, entityId, account_type: "EXCLUDE_STRIPE" },
    { skip: !groupId || !entityId }
  );

  const { fromIds, manualEntry, bankStatement } = useSelector(
    (state: RootState) => state.openItemFilter
  );

  const banksByName = useMemo(
    () =>
      froms.reduce((acc, cc) => {
        if (!acc[cc.brand_name]) {
          acc[cc.brand_name] = [];
        }
        acc[cc.brand_name].push(cc);
        return acc;
      }, {} as Record<string, SourceFilter[]>),
    [isSuccess]
  );

  const onBankNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;
    const allBanks = new Set(banksByName[name].map(({ uuid }) => uuid));

    let newSelectedIds: Set<string>;
    if (checked) {
      newSelectedIds = new Set([...fromIds, ...allBanks]);
    } else {
      newSelectedIds = new Set(fromIds.filter((id) => !allBanks.has(id)));
    }

    dispatch(setOpenItemFilter({ fromIds: [...newSelectedIds] }));
  };

  const onHandleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    let newSelectedIds: Set<string>;
    if (checked) {
      newSelectedIds = new Set([...fromIds, value]);
    } else {
      newSelectedIds = new Set(fromIds.filter((id) => id !== value));
    }

    dispatch(setOpenItemFilter({ fromIds: [...newSelectedIds] }));
  };

  const onOtherHandleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (name === "Manual Entry") {
      dispatch(setOpenItemFilter({ manualEntry: checked }));
    } else if (name === "Bank statement") {
      dispatch(setOpenItemFilter({ bankStatement: checked }));
    }
  };

  return (
    <div className="t-w-full t-flex t-flex-col t-gap-5">
      <div className="t-text-body-sm t-text-text-30">Sent from</div>
      <div className="t-flex t-flex-col t-gap-4">
        <Async.Root
          isLoading={isLoading}
          isSuccess={isSuccess}
          isEmpty={froms.length === 0}
          loaderType="secondary"
        >
          <Async.Empty>
            <></>
          </Async.Empty>
          <Async.Success>
            {Object.entries(banksByName).map(([name, banks]) => {
              const checked = banks.every((bank) =>
                new Set(fromIds).has(bank.uuid)
              );
              const indeterminate =
                banks.some((bank) => new Set(fromIds).has(bank.uuid)) &&
                !checked;

              return (
                <span className="t-flex t-flex-col t-gap-4" key={name}>
                  <Checkbox
                    name={name}
                    label={<span className="t-text-text-30">{name}</span>}
                    value={name}
                    onChange={onBankNameChange}
                    checked={checked}
                    indeterminate={indeterminate}
                  />
                  <span className="t-ml-6 t-flex t-flex-col t-gap-4">
                    {banks.map((from) => (
                      <Checkbox
                        key={from.uuid}
                        name={from.uuid}
                        label={
                          <>
                            {from?.nickname}
                            {from?.mask && <> - {from.mask}</>}
                          </>
                        }
                        value={from.uuid}
                        checked={new Set(fromIds).has(from.uuid)}
                        onChange={onHandleChange}
                      />
                    ))}
                  </span>
                  <Divider color="grey" />
                </span>
              );
            })}
          </Async.Success>
        </Async.Root>
        <Checkbox
          name="Manual Entry"
          label="Manual Entry"
          value="Manual Entry"
          checked={manualEntry}
          onChange={onOtherHandleChange}
        />
        <Checkbox
          name="Bank statement"
          label="Bank statement"
          value="Bank statement"
          checked={bankStatement}
          onChange={onOtherHandleChange}
        />
      </div>
    </div>
  );
};

const OpenItemAmountFilter = () => {
  const dispatch = useDispatch();
  const { cashFlow, minAmount, maxAmount } = useSelector(
    (state: RootState) => state.openItemFilter
  );

  const handleRadioCheck = (value: string) => {
    if (value === "ALL") {
      dispatch(
        setOpenItemFilter({ cashFlow: "", minAmount: "", maxAmount: "" })
      );
      return;
    }
    dispatch(setOpenItemFilter({ cashFlow: value }));
  };

  const handleAmountChange = debounce((e: ChangeEvent<HTMLFormElement>) => {
    const { value, name } = e.target;
    if (name === "minAmount") {
      dispatch(setOpenItemFilter({ minAmount: value }));
    } else if (name === "maxAmount") {
      dispatch(setOpenItemFilter({ maxAmount: value }));
    }
  });

  return (
    <Formik
      initialValues={{ minAmount: minAmount || "", maxAmount: maxAmount || "" }}
      onSubmit={() => {}}
    >
      {() => (
        <div className="t-flex t-flex-col t-gap-5">
          <div className="t-flex t-flex-col t-gap-3">
            <div className="t-text-body-sm t-text-neutral-80 t-w-full">
              Type
            </div>
            <Radio.Root
              onValueChange={handleRadioCheck}
              defaultValue={cashFlow || "ALL"}
            >
              <Radio.Content>
                <Radio.Item asChild value={CASH_FLOW.ALL}>
                  {CASH_FLOW_LABEL[CASH_FLOW.ALL]}
                </Radio.Item>
                <Radio.Item asChild value={CASH_FLOW.CREDIT}>
                  {CASH_FLOW_LABEL[CASH_FLOW.CREDIT]}
                </Radio.Item>
                <Radio.Item asChild value={CASH_FLOW.DEBIT}>
                  {CASH_FLOW_LABEL[CASH_FLOW.DEBIT]}
                </Radio.Item>
              </Radio.Content>
            </Radio.Root>
            <Form
              className="all:unset t-flex t-flex-col t-gap-4"
              onChange={handleAmountChange}
            >
              <PriceInput
                name="minAmount"
                placeholder="0.00"
                label="Min. Amount"
                disabled={!cashFlow}
              />
              <PriceInput
                name="maxAmount"
                placeholder="0.00"
                label="Max. Amount"
                disabled={!cashFlow}
              />
            </Form>
          </div>
        </div>
      )}
    </Formik>
  );
};

const OpenItemCapsuleFilters = () => {
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.openItemFilter);
  const {
    startDate,
    endDate,
    vendors,
    categoryIds,
    fromIds,
    manualEntry,
    bankStatement,
    minAmount,
    maxAmount,
  } = filters;

  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const merchantIds = vendors.join(",");
  const { data: selectedVendorsData } = useGetAllTransactionMerchantsQuery(
    { groupId, entityId, merchantIds },
    { skip: !merchantIds || !groupId || !entityId }
  );

  const hasDate = Boolean(startDate || endDate);
  const hasSource = fromIds.length > 0 || manualEntry || bankStatement;
  const hasAmount = Boolean(minAmount || maxAmount);

  const dateLabel = (() => {
    if (startDate && endDate) {
      return `${dayjs(startDate).format(DISPLAY_DATE)} - ${dayjs(
        endDate
      ).format(DISPLAY_DATE)}`;
    }
    if (startDate) return `From ${dayjs(startDate).format(DISPLAY_DATE)}`;
    if (endDate) return `To ${dayjs(endDate).format(DISPLAY_DATE)}`;
    return "";
  })();

  return (
    <>
      {hasDate && (
        <Capsule
          value={dateLabel}
          onCapsuleClick={() =>
            dispatch(
              setOpenItemFilter({
                startDate: "",
                endDate: "",
                selectPeriod: "",
              })
            )
          }
          type="transactionDate"
        >
          Date:
        </Capsule>
      )}
      {vendors.map((vendor) => {
        const merchant = selectedVendorsData?.merchants.find(
          (m) => m.uuid === vendor
        );
        return (
          <Capsule
            key={vendor}
            value={merchant?.name || vendor}
            onCapsuleClick={() =>
              dispatch(
                setOpenItemFilter({
                  vendors: vendors.filter((v) => v !== vendor),
                })
              )
            }
            type="vendors"
          >
            {""}
          </Capsule>
        );
      })}
      {categoryIds.length > 0 && (
        <Capsule
          value={`(${categoryIds.length})`}
          onCapsuleClick={() =>
            dispatch(setOpenItemFilter({ categoryIds: [] }))
          }
          type="category"
        >
          Categories
        </Capsule>
      )}
      {hasSource && (
        <Capsule
          value="(1)"
          onCapsuleClick={() =>
            dispatch(
              setOpenItemFilter({
                fromIds: [],
                manualEntry: false,
                bankStatement: false,
              })
            )
          }
          type="from"
        >
          Source
        </Capsule>
      )}
      {hasAmount && (
        <Capsule
          value={minAmount || maxAmount}
          onCapsuleClick={() =>
            dispatch(
              setOpenItemFilter({ minAmount: "", maxAmount: "", cashFlow: "" })
            )
          }
          type="amount"
        >
          Amount
        </Capsule>
      )}
    </>
  );
};

const countActiveFilters = (filters: OpenItemFilterState): number => {
  let count = 0;
  if (filters.startDate || filters.endDate) count += 1;
  if (filters.vendors.length > 0) count += 1;
  if (filters.categoryIds.length > 0) count += 1;
  if (
    filters.fromIds.length > 0 ||
    filters.manualEntry ||
    filters.bankStatement
  )
    count += 1;
  if (filters.minAmount || filters.maxAmount) count += 1;
  return count;
};

const OpenItemsFilter = () => {
  const dispatch = useDispatch();
  const [value, setValue] = useState("transactionDate");
  const [open, setOpen] = useState(false);

  const filters = useSelector((state: RootState) => state.openItemFilter);
  const { hasNoVendors } = filters;
  const appliedFilterCount = countActiveFilters(filters);

  const onSelect = (val: string) => {
    if (val) setValue(val);
  };

  const onOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  return (
    <FilterContext.Provider value={{ value, onSelect, setOpen, open }}>
      <Dropdown.Root open={open} onOpenChange={onOpenChange} modal={false}>
        <Dropdown.Trigger asChild>
          <Button
            size="xsmall"
            customType={appliedFilterCount > 0 ? "secondary" : "icon"}
          >
            <div className="t-flex t-items-center t-text-text-30 t-gap-1">
              <Funnel />
              {appliedFilterCount > 0 && `(${appliedFilterCount})`}
            </div>
          </Button>
        </Dropdown.Trigger>
        <OpenItemCapsuleFilters />
        {/* Quick filter is not worth the row space on mobile */}
        <div className="t-hidden md:t-flex t-items-center">
          <Chip
            type="button"
            isActive={hasNoVendors}
            isFixedFilter
            filterType="others"
            size="xsmall"
            onClick={() => dispatch(setOpenItemFilter({ hasNoVendors: true }))}
            onRemove={() =>
              dispatch(setOpenItemFilter({ hasNoVendors: false }))
            }
          >
            Unknown Vendors
          </Chip>
        </div>
        <Dropdown.Portal>
          <Filter.Portal>
            <Filter.List>
              <Filter.ListItem value="transactionDate">Date</Filter.ListItem>
              <Filter.ListItem value="vendors">Vendors</Filter.ListItem>
              <Filter.ListItem value="category">Category</Filter.ListItem>
              <Filter.ListItem value="from">Source</Filter.ListItem>
              <Filter.ListItem value="amount">Amount</Filter.ListItem>
            </Filter.List>
            <Filter.Body value="transactionDate" block>
              <OpenItemDateFilter />
            </Filter.Body>
            <Filter.Body value="vendors" block>
              <OpenItemVendorsFilter />
            </Filter.Body>
            <Filter.Body value="category" block>
              <OpenItemCategoryFilter />
            </Filter.Body>
            <Filter.Body value="from" block>
              <OpenItemFromFilter />
            </Filter.Body>
            <Filter.Body value="amount" block>
              <OpenItemAmountFilter />
            </Filter.Body>
          </Filter.Portal>
        </Dropdown.Portal>
      </Dropdown.Root>
    </FilterContext.Provider>
  );
};

export default OpenItemsFilter;
